import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { KrogerInfoBox } from "./KrogerInfoBox";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { Divider } from "primereact/divider";
import commodities from "./commodities.json";

const fieldInfo = require('../../assets/intake-form-field-info.png');

const { REACT_APP_THINKHAUS_URL } = process.env

const KrogerIntakeForm = () => {
    const initialFormState = {
        UPC: [""],
        commodity: "",
        commodityManager: "",
        subCommodity: [""],
        effectiveDate: null,
        endDate: null,
        field: [
            { type: "Product Title" },
            { type: "Customer Facing Size" },
            { type: "Product Description (paragraph and feature bullet points)" },
            { type: "Package Shot Image" },
            { type: "Nutrition" },
            { type: "Ingredients" },
            { type: "Allergens" },
            { type: "Category Standards" },
            { type: "Enhanced Image: MRHI" },
            { type: "Enhanced Image: Carousels" },
            { type: 'Other' }
        ],
        userName: "",
        email: "",
        role: [
            { name: "Our Brands Brand Manager" },
            { name: "Our Brands CSM/Acceleration Team" },
            { name: "Digital DCM/ADCM" },
            { name: "CFT Regulatory" },
            { name: "Legal" },
            { name: "Supplier" },
            { name: "Other" },
        ],
        specifiedRole: '',
        isHighPriority: false,
        issue: "",
        reason: "",
        files: [],
        otherEmailsToNotify: [""],
        selectedRole: { name: "" },
        selectedFields: [],
        period: 0,
        week: 0,
    };

    const [formState, setFormState] = useState(initialFormState);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [error, setError] = useState(null);

    const errorRef = useRef(null);
    const fileUploadRef = useRef(null);

    // Focus on when error changes and is not null
    useEffect(() => {
        if (error && errorRef.current) {
            // Scrolls into view and sets focus
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            errorRef.current.focus();
        }
    }, [error]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({ ...prevState, [name]: value }));

        if (name === "email") {
            setIsValidEmail(true);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setIsValidEmail(emailRegex.test(value));
        }
        if (name === "isHighPriority") {
            setFormState((prevState) => ({ ...prevState, isHighPriority: e.value }));
        }
    };

    const handleNumberChange = (key: string, e: InputNumberChangeEvent) => {
        const { value } = e;
        setFormState((prevState) => ({ ...prevState, [key]: value }));
    }

    const handleSelect = (e) => {
        const { name, value } = e?.target;
        setFormState((prevState) => ({ ...prevState, [name]: value }));

        // clear out specifiedRole if selectedRole option is chosen
        if (name === 'selectedRole') {
            if (formState.selectedRole.name === 'Other') {
                setFormState((prevState) => ({ ...prevState, specifiedRole: '' }));
            }
        }
    };

    const handleDateChange = (e) => {
        setFormState((prevState) => ({ ...prevState, effectiveDate: e.value }));
    };

    const handleEndDateChange = (e) => {
        setFormState((prevState) => ({ ...prevState, endDate: e.value }));
    };


    const setEffectiveDate = (effectiveDate) => {
        if (effectiveDate) {
            return effectiveDate?.toISOString();
        }
        return "";
    }

    function fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
        });
    }

    const handleFileUpload = async (e) => {
        const files = Array.from(e.files); // Assuming e.target.files is the FileList

        // Convert all files to base64
        const promises = files.map(file => fileToBase64(file));
        const base64Files = await Promise.all(promises);

        // Update form state with base64 strings
        setFormState(prevState => ({
            ...prevState,
            files: base64Files.map(base64 => ({ base64 }))
        }));
    };

    const splitMultipleValues = string => string.split(/(?:, | )/g)

    const handleDynamicFields = (field, title) => {
        const items = formState?.[field];

        const handleFieldChange = (index, field, value) => {
            setFormState((prevState) => ({
                ...prevState,
                [field]: prevState[field].map((item, i) =>
                    i === index ? value : item
                ),
            }));
        };

        return (
            <div className="w-full">
                {items.map((item, index) => (
                    <div key={index} className="mb-3">
                        {field !== 'otherEmailsToNotify' ?
                            <div className="p-float-label">
                                <InputText
                                    id={`${field}-${index}`}
                                    className="w-full"
                                    value={item}
                                    onChange={(e) =>
                                        handleFieldChange(index, field, e.target.value)
                                    }
                                    disabled={field !== "otherEmailsToNotify"}
                                    data-cy-title
                                />
                                <label htmlFor={`${field}-${index}`}>{title}{field !== "otherEmailsToNotify" ? <span className="text-red-500">*</span> : ''}</label>
                            </div>
                            :
                            <div>
                                <label className="block text-sm font-medium text-700 mb-2">{title}</label>
                                <Chips
                                    className="w-full"
                                    value={item}
                                    onChange={(e) =>
                                        handleFieldChange(index, field, e.target.value)
                                    }
                                    disabled={field !== "otherEmailsToNotify"}
                                    placeholder="Type and press Enter"
                                />
                                <p className="text-xs text-500 mt-1 mb-0">Press Enter after each email to confirm.</p>
                            </div>
                        }
                    </div>
                ))}
            </div>
        );
    };

    const handleSubmit = () => {
        const {
            effectiveDate,
            endDate,
            userName,
            email,
            selectedRole,
            selectedFields,
            specifiedRole,
            issue,
            reason,
            files,
            period,
            week,
            otherEmailsToNotify,
            UPC,
            commodity,
            commodityManager,
            isHighPriority
        } = formState;


        let dataIn = {
            // the Sendgrid route still expects GTIN key though client requests UPC
            GTIN: splitMultipleValues(UPC[0]),
            commodity: splitMultipleValues(commodity),
            commodityManager,
            effectiveDate: setEffectiveDate(effectiveDate),
            endDate,
            dateRequested: new Date().toISOString().split('T')[0],
            fields: selectedFields.map(field => field.type),
            userName,
            email,
            role: selectedRole.name,
            specifiedRole,
            issue,
            reason,
            period,
            week,
            files,
            isEnhancedImage: formState.selectedFields.some(field => field.type.startsWith('Enhanced Image')),
            otherEmailsToNotify,
            isHighPriority
        };

        const requiredFields = [
            'period',
            'week',
            'email',
            'userName',
        ]

        const missingRequiredFields = requiredFields.filter(field => !dataIn[field]);

        if (missingRequiredFields.length) {
            setIsConfirming(false);
            setError({ message: `Please fill out missing fields: ${missingRequiredFields.join(', ')}` });
            return;
        }

        const requiredProductFields = [
            { key: 'GTIN', label: 'UPC' },
            { key: 'commodity', label: 'Commodity' }
        ];

        const missingProductFields = requiredProductFields
            .filter(({ key }) => {
                const value = dataIn[key][0];
                return value === null || value === undefined || value === '';
            });

        if (missingProductFields.length) {
            setIsConfirming(false);
            setError({ message: `Please fill out missing fields: ${missingProductFields.map(field => field.label).join(', ')}` });
            return;
        }

        const entries = Object.entries(dataIn);
        for (const [key, value] of entries) {
            if (Array.isArray(value)) {
                dataIn = {
                    ...dataIn,
                    [key]: value.filter((item) => item !== ""),
                };
            }
        }

        setIsConfirming(false);
        setIsLoading(true);
        setError(null);

        axios({
            method: "post",
            url: REACT_APP_THINKHAUS_URL + "kroger-intake-form",
            data: dataIn,
        })
            .then((response) => {
                setShowModal(true);
                console.log("Form submitted successfully:", response.data);
            })
            .catch((error) => {
                setError(error);
                console.error("Error submitting form:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onCloseModal = ({ isAnotherIssue }) => {
        if (isAnotherIssue) {
            setFormState({
                ...initialFormState,
                userName: formState.userName,
                email: formState.email,
                selectedRole: formState.selectedRole,
            });
        } else {
            setFormState(initialFormState);
        }

        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }

        setShowModal(false);
    };

    const handleUPCChange = (value: string) => {
        setFormState((prevState) => ({
            ...prevState,
            UPC: [value],
        }));
    };

    const handleCommodityChange = (value) => {
        const commodityRecord = commodities.find(commodity => commodity.commodity === value);
        setFormState({
            ...formState,
            commodity: value,
            commodityManager: commodityRecord ? commodityRecord.commodityManager : '',
        });
    };

    const required = (<span className="text-red-500">*</span>)

    const managers = useMemo(() => {
        return Array.from(new Set(commodities.filter(commodity => commodity.commodityManager).map(commodity => commodity.commodityManager)));
    }, [commodities]);

    return (
        <div className="surface-ground font-kroger min-h-screen py-5 px-3">
            <Tooltip target=".size-label" position="bottom">
                <img className="h-25rem" src={fieldInfo} alt="info on PDP fields" />
            </Tooltip>
            <Tooltip target=".priority-info" />

            <div className="mx-auto" style={{ maxWidth: '960px' }}>
                {/* Header */}
                <div className="text-center mb-5">
                    <h1 className="text-4xl font-bold m-0" style={{ color: '#084999' }}>
                        Our Brands Change Request
                    </h1>
                    <p className="text-xl text-600 mt-2 mb-0">Intake Form</p>
                </div>

                {/* Info Banner */}
                <KrogerInfoBox className="mb-5">
                    <div>
                        Please fill out the following form to request changes to Our Brands Product Detail Page
                        (PDP) fields such as product title, description, customer-facing size, and images{' '}<span className="size-label w-max"><i className="pi pi-info-circle" /></span>.
                        Changes will be reflected on site on the provided date or within 10 business days of
                        submission. Confirmation emails will be sent to you and specified relevant parties to confirm
                        receipt of your request and when the changes are implemented.
                    </div>
                </KrogerInfoBox>

                <p className="text-sm text-600 mb-4">{required}{' '} Indicates a required field.</p>

                {/* Product Identification Section */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center gap-2 mb-4">
                        <i className="pi pi-barcode text-2xl" style={{ color: '#084999' }}></i>
                        <h2 className="text-xl font-semibold m-0" style={{ color: '#084999' }}>Product Identification</h2>
                    </div>

                    <div data-cy-upc>
                        <div className="p-float-label mt-3">
                            <InputText
                                id="upc-input"
                                className={`w-full ${formState.UPC[0] === '' ? 'p-invalid' : ''}`}
                                value={formState.UPC.join(', ')}
                                onChange={(e) => handleUPCChange(e.target.value)}
                                name="UPC"
                                data-cy-upc-input
                            />
                            <label htmlFor="upc-input">UPC(s){required}</label>
                        </div>

                        <div className="grid mt-3">
                            <div className="col-12 md:col-7">
                                <div className="p-float-label">
                                    <Dropdown
                                        inputId="commodity-dropdown"
                                        className={`w-full ${formState.commodity ? '' : 'p-invalid'}`}
                                        filter
                                        value={formState.commodity}
                                        options={commodities.map(c => ({ label: c.commodityDescription, value: c.commodity }))}
                                        onChange={(e) => handleCommodityChange(e.value)}
                                        name="commodity"
                                        data-cy-commodity-dropdown
                                    />
                                    <label htmlFor="commodity-dropdown">Commodity{required}</label>
                                </div>
                            </div>
                            <div className="col-12 md:col-5">
                                <div className="p-float-label">
                                    <Dropdown
                                        inputId="manager-dropdown"
                                        className={`w-full ${formState.commodityManager ? '' : 'p-invalid'}`}
                                        filter
                                        value={formState.commodityManager}
                                        options={managers.map(m => ({ label: m, value: m }))}
                                        onChange={(e) => handleChange({ target: { name: 'commodityManager', value: e.value } })}
                                        name="commodityManager"
                                        data-cy-commodity-manager-dropdown
                                    />
                                    <label htmlFor="manager-dropdown">Manager (DPM){required}</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-500 mt-3 mb-0 line-height-3">
                        <i className="pi pi-info-circle mr-1 text-xs"></i>
                        Enter UPCs with or without leading zeros. Separate multiple entries by spaces (# # #) or commas (#,#,#).
                    </p>
                </Card>

                {/* Kompass Period Section */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center gap-2 mb-4">
                        <i className="pi pi-calendar text-2xl" style={{ color: '#084999' }}></i>
                        <h2 className="text-xl font-semibold m-0" style={{ color: '#084999' }}>Kompass Period Intro Date</h2>
                    </div>

                    <div className="grid mt-3">
                        <div className="col-12 md:col-6" data-cy-periodnumber>
                            <div className="p-float-label">
                                <InputNumber
                                    inputId="period-input"
                                    className={`w-full ${formState.period ? '' : 'p-invalid'}`}
                                    value={formState.period}
                                    onChange={(e) => handleNumberChange('period', e)}
                                    name="period"
                                />
                                <label htmlFor="period-input">Period Number{required}</label>
                            </div>
                        </div>
                        <div className="col-12 md:col-6" data-cy-weeknumber>
                            <div className="p-float-label">
                                <InputNumber
                                    inputId="week-input"
                                    className={`w-full ${formState.week ? '' : 'p-invalid'}`}
                                    value={formState.week}
                                    onChange={(e) => handleNumberChange('week', e)}
                                    name="week"
                                />
                                <label htmlFor="week-input">Week Number{required}</label>
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 p-3 border-round-lg surface-100 text-center">
                        <p className="text-sm text-600 m-0 line-height-3">
                            Requests submitted more than 4 weeks before on-shelf date will not be processed until within the 4-week window.
                            <br />
                            <span className="font-italic">Note: NPD Images only appear online if planogrammed, or once sales post in the division after 7 days.</span>
                        </p>
                    </div>
                </Card>

                {/* Request Details Section */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center gap-2 mb-4">
                        <i className="pi pi-user text-2xl" style={{ color: '#084999' }}></i>
                        <h2 className="text-xl font-semibold m-0" style={{ color: '#084999' }}>Request Details</h2>
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6" data-cy-username>
                            <div className="p-float-label">
                                <InputText
                                    id="username-input"
                                    className="w-full"
                                    value={formState.userName}
                                    onChange={handleChange}
                                    name="userName"
                                />
                                <label htmlFor="username-input">Name{required}</label>
                            </div>
                        </div>
                        <div className="col-12 md:col-6" data-cy-email>
                            <div className="p-float-label">
                                <InputText
                                    id="email-input"
                                    className={`w-full ${isValidEmail ? '' : 'p-invalid'}`}
                                    value={formState.email}
                                    onChange={handleChange}
                                    name="email"
                                    type="email"
                                />
                                <label htmlFor="email-input">Email{required}</label>
                            </div>
                        </div>
                        <div className="col-12 md:col-6 mt-4">
                            <Dropdown
                                value={formState.selectedRole}
                                onChange={handleSelect}
                                options={formState.role}
                                optionLabel="name"
                                placeholder="Select Your Role*"
                                className="w-full"
                                name="selectedRole"
                                data-cy-selectedrole
                            />
                            {formState.selectedRole.name === 'Other' &&
                                <div className="mt-3 p-float-label">
                                    <InputText
                                        id="specified-role"
                                        className="w-full"
                                        value={formState.specifiedRole}
                                        name="specifiedRole"
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="specified-role">Please Specify</label>
                                </div>
                            }
                        </div>
                        <div className="col-12 md:col-6 mt-4">
                            <MultiSelect
                                display="chip"
                                value={formState.selectedFields}
                                onChange={handleSelect}
                                options={formState.field}
                                placeholder="Select Field(s)*"
                                optionLabel="type"
                                className="w-full"
                                name="selectedFields"
                                data-cy-selectedfields
                            />
                        </div>
                    </div>
                </Card>

                {/* Dates & Notifications Section */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center gap-2 mb-4">
                        <i className="pi pi-clock text-2xl" style={{ color: '#084999' }}></i>
                        <h2 className="text-xl font-semibold m-0" style={{ color: '#084999' }}>Dates & Notifications</h2>
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6" data-cy-targetdate>
                            <label className="block text-sm font-medium text-700 mb-2">Target Date{required}</label>
                            <Calendar
                                className="w-full"
                                value={formState.effectiveDate}
                                onSelect={handleDateChange}
                                dateFormat="yy-mm-dd"
                                showIcon
                                name="effectiveDate"
                                placeholder="Select today for ASAP"
                            />
                        </div>
                        <div className="col-12 md:col-6" data-cy-enddate>
                            <label className="block text-sm font-medium text-700 mb-2">End Date</label>
                            <Calendar
                                className="w-full"
                                minDate={formState.effectiveDate}
                                value={formState.endDate}
                                onSelect={handleEndDateChange}
                                dateFormat="yy-mm-dd"
                                showIcon
                                name="endDate"
                                placeholder="End date for change"
                            />
                        </div>
                        <div className="col-12 mt-4">
                            {handleDynamicFields("otherEmailsToNotify", "Other Emails To Receive Confirmation Emails")}
                        </div>
                    </div>
                </Card>

                {/* Description Section */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center gap-2 mb-4">
                        <i className="pi pi-pencil text-2xl" style={{ color: '#084999' }}></i>
                        <h2 className="text-xl font-semibold m-0" style={{ color: '#084999' }}>Description</h2>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-700 mb-2">
                            What change are you requesting?{required}
                        </label>
                        <p className="text-xs text-500 mt-0 mb-2">Include sufficient detail to reduce back-and-forth clarification.</p>
                        <InputTextarea
                            className="w-full"
                            name="issue"
                            onChange={handleChange}
                            placeholder="Describe the change..."
                            rows={4}
                            value={formState.issue}
                            autoResize
                            data-cy-description
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-700 mb-2">
                            Why are you requesting this change?
                        </label>
                        <p className="text-xs text-500 mt-0 mb-2">Helps us understand the need state and identify larger issues.</p>
                        <InputTextarea
                            className="w-full"
                            name="reason"
                            onChange={handleChange}
                            placeholder="Describe the reason..."
                            rows={3}
                            value={formState.reason}
                            autoResize
                            data-cy-reason
                        />
                    </div>

                    <Divider />

                    <div data-cy-fileupload>
                        <label className="block text-sm font-medium text-700 mb-2">
                            <i className="pi pi-paperclip mr-1"></i>Attachments
                        </label>
                        <p className="text-xs text-500 mt-0 mb-3">Upload screenshots or files to provide additional clarity (e.g. circle the problem, mockup of your proposed solution).</p>
                        <FileUpload
                            ref={fileUploadRef}
                            uploadHandler={handleFileUpload}
                            name="demo[]"
                            multiple
                            maxFileSize={1000000}
                            customUpload
                            auto
                            emptyTemplate={
                                <div className="flex align-items-center flex-column py-4">
                                    <i className="pi pi-cloud-upload text-4xl text-400 mb-2"></i>
                                    <p className="m-0 text-500">Drag and drop files here to upload</p>
                                </div>
                            }
                        />
                    </div>
                </Card>

                {/* Priority & Submit */}
                <Card className="mb-4 shadow-2">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-4">
                        <div className="flex align-items-center gap-3">
                            <InputSwitch
                                data-testid='highPrioritySwitch'
                                name="isHighPriority"
                                checked={formState.isHighPriority}
                                onChange={handleChange}
                            />
                            <div>
                                <label className="font-semibold text-900 cursor-pointer" htmlFor="isHighPriority">
                                    High Priority Request
                                    <i
                                        className="priority-info ml-2 pi pi-info-circle text-500"
                                        data-pr-position="top"
                                        data-pr-tooltip="High priority requests pose a legal or sales risk to the business if they are not addressed immediately. These will be sent to the top of the queue and may cause delays with other requests."
                                    />
                                </label>
                                {formState.isHighPriority && (
                                    <Tag severity="danger" value="HIGH PRIORITY" className="ml-2" />
                                )}
                            </div>
                        </div>

                        <Button
                            className="px-5 py-3 border-round-lg font-semibold text-lg shadow-2"
                            style={{ backgroundColor: '#084999', borderColor: '#084999' }}
                            onClick={() => setIsConfirming(true)}
                            label={isLoading ? "Submitting..." : "Submit Request"}
                            icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-send"}
                            disabled={isLoading || !isValidEmail}
                            data-cy-submitform
                        />
                    </div>
                </Card>

                {/* Error Display */}
                {error && (
                    <div
                        ref={errorRef}
                        className="p-3 border-round-lg border-1 border-red-300 bg-red-50 flex align-items-center gap-2 mb-4"
                        tabIndex={-1}
                        data-cy-errortext
                    >
                        <i className="pi pi-exclamation-triangle text-red-500 text-xl"></i>
                        <span className="text-red-700 font-semibold">{error.message}</span>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            <Dialog
                header={<span className="font-semibold">Confirm Submission</span>}
                visible={isConfirming}
                style={{ maxWidth: '400px', width: '100%' }}
                onHide={() => setIsConfirming(false)}
                modal
                draggable={false}
            >
                <div className="text-center py-3">
                    <i className="pi pi-question-circle text-5xl mb-3" style={{ color: '#084999' }}></i>
                    <p className="text-lg text-700 mb-4">Are you sure you want to submit this request?</p>
                    <div className="flex justify-content-center gap-3">
                        <Button
                            onClick={() => handleSubmit()}
                            icon="pi pi-check"
                            label="Submit"
                            className="px-4"
                            style={{ backgroundColor: '#084999', borderColor: '#084999' }}
                            data-cy-confirmsubmit
                        />
                        <Button
                            onClick={() => setIsConfirming(false)}
                            icon="pi pi-times"
                            label="Cancel"
                            className="p-button-outlined px-4"
                            style={{ color: '#084999', borderColor: '#084999' }}
                        />
                    </div>
                </div>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                header={<span className="font-semibold text-green-600"><i className="pi pi-check-circle mr-2"></i>Success!</span>}
                visible={showModal}
                style={{ maxWidth: '400px', width: '100%' }}
                onHide={() => onCloseModal({ isAnotherIssue: false })}
                modal
                draggable={false}
            >
                <div className="text-center py-3">
                    <p className="text-lg text-700 mb-4">Your request has been submitted. Would you like to enter another issue?</p>
                    <div className="flex justify-content-center gap-3">
                        <Button
                            onClick={() => onCloseModal({ isAnotherIssue: true })}
                            icon="pi pi-plus"
                            label="New Request"
                            className="px-4"
                            style={{ backgroundColor: '#084999', borderColor: '#084999' }}
                            data-cy-successyes
                        />
                        <Button
                            onClick={() => onCloseModal({ isAnotherIssue: false })}
                            icon="pi pi-times"
                            label="Done"
                            className="p-button-outlined px-4"
                            style={{ color: '#084999', borderColor: '#084999' }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default KrogerIntakeForm;
